<script setup lang="ts">
import { type ProductResponse } from "../../../responses/ProductResponse";
import { computed, ref, type Ref, onMounted } from "vue";
import { label } from "../../mixins/translations";
import { useProductStore } from "../../stores/product";
import { type ProductVariantPropertyResponse } from "../../../responses/ProductVariantPropertyResponse";
import { type ProductVariantResponse } from "../../../responses/ProductVariantResponse";
import { sortBy } from "lodash";
import ReviewList from "./ReviewList.vue";
import type { DebtorContactResponse } from "../../../responses/Site/DebtorContactResponse";
import {PlusIcon, MinusIcon } from "lucide-vue-next";

type ProductVariantProperties = { [key: string]: Array<ProductVariantPropertyResponse> };

let tabs = [
    "description",
];

interface Props {
    product: ProductResponse;
    recaptchaKey: string;
    user?: DebtorContactResponse;
}

const props = defineProps<Props>();

const productStore = useProductStore();
const selectedTab: Ref<(typeof tabs)[number]> = ref("description");

const selectedVariantProperties: Ref<ProductVariantProperties> = ref({});

const faqs = ref(
    (props.product.faqs || []).map((f: any) => ({
        ...f,
        toggled: !!f.toggled,
    }))
);

productStore.events.on("selectedVariantChanged", async (variant: ProductVariantResponse) => {
    if(variant) {
        productStore.getVariantProperties(variant).then(function (response: ProductVariantProperties) {
            selectedVariantProperties.value = response || {};
        });
    }
});
productStore.events.on("goToReviews", () => {
    selectedTab.value = 'reviews';
})

onMounted(async () => {
    if(props.product.usps.length) tabs.push('usps');
    tabs.push('properties');
    if(props.product.attachments.length) tabs.push('manuals');
    if(props.product.faqs.length) tabs.push('faqs');
    tabs.push('reviews'); //show reviews last
});

const sortedUsps = computed(() => {
    return sortBy(props.product.usps || [], (usp: any) => !usp.is_positive);
});
</script>

<template>
    <!-- Tabs -->
    <div
        class="flex overflow-x-auto lg:flex-row space-x-1 rounded-none ml-0"
        role="tablist"
        aria-label="Product tabs"
    >
        <button
            v-for="tab in tabs"
            :key="tab"
            type="button"
            class="px-7 py-3 bg-white/20 text-left transition-colors duration-200 text-black hover:text-black text-base lg:text-lg whitespace-nowrap"
            :class="{
                '!font-bold text-black !bg-white': selectedTab === tab,
                'font-medium !bg-white/40': selectedTab !== tab
            }"
            @click="selectedTab = tab"
            role="tab"
            :aria-selected="selectedTab === tab"
            :aria-controls="`panel-${tab}`"
        >
            {{ label('site.product.detail.' + tab) }}
        </button>
    </div>

    <!-- Panels -->
    <div class="bg-white p-6 lg:p-10 rounded-none text-base" style="margin-top: -1px;">
        <!-- Description -->
        <div :id="`panel-description`" role="tabpanel" v-show="selectedTab === 'description'">
            <div class="font-normal leading-relaxed text-lg lg:columns-2" v-html="product.description_long"></div>
        </div>

        <!-- USPs -->
        <div :id="`panel-usps`" role="tabpanel" v-show="selectedTab === 'usps'">
            <div class="font-normal leading-relaxed text-lg">
                <ul v-if="sortedUsps && sortedUsps.length" class="flex flex-col">
                    <li
                        v-for="(usp, i) in sortedUsps"
                        :key="usp.id ?? i"
                        class="flex gap-1 items-center py-1"
                    >
                        <span
                            class="mr-3 flex-shrink-0 text-primary"
                        >
                            <PlusIcon v-if="usp.is_positive"></PlusIcon>
                            <MinusIcon v-else></MinusIcon>
                        </span>
                        <p v-html="usp.description"></p>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Properties -->
        <div :id="`panel-properties`" role="tabpanel" v-show="selectedTab === 'properties'">
            <div
                class="max-w-2xl font-normal leading-relaxed text-base specifications"
                v-for="(productVariantProperties, category) in selectedVariantProperties"
                :key="category"
            >
                <h3 class="text-lg text-[#1A1A1C] font-bold mt-2">{{ category }}</h3>
                <ul class="my-4">
                    <li
                        v-for="(productVariantProperty, idx) in productVariantProperties"
                        :key="productVariantProperty.id ?? productVariantProperty.property?.name ?? idx"
                        class="flex flex-row leading-8 specificationItem odd:bg-[#F8F7F4]"
                    >
                        <span class="w-3/6 text-base px-4 p-2" v-html="productVariantProperty.property.name"></span>
                        <span class="w-3/6 text-base px-4 p-2" v-html="productVariantProperty.value"></span>
                    </li>
                </ul>
            </div>
        </div>

        <div :id="`panel-manuals`" role="tabpanel" v-show="selectedTab === 'manuals'">
            <div class="text-base text-gray-700">
                <slot name="manuals">
                    <p class="text-gray-500">Handleidingen worden hier getoond.</p>
                </slot>
            </div>
        </div>

        <div :id="`panel-faqs`" role="tabpanel" v-show="selectedTab === 'faqs'">
            <div class="font-normal leading-relaxed text-base">
                <div v-for="(faq, i) in faqs" :key="faq.id ?? i">
                    <h4
                        class="hover:cursor-pointer text-base lg:text-lg border-b border-secondary-100 py-3 flex items-center w-full"
                        @click="faq.toggled = !faq.toggled"
                        :aria-expanded="faq.toggled ? 'true' : 'false'"
                    >
                    <span class="icon">
                      <svg
                          id="icon-faq"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="lucide text-primary lucide-chevron-right transition-transform"
                          :class="{ 'rotate-90': faq.toggled }"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </span>
                        <span class="ml-2 text-secondary-900 font-semibold text-base" v-text="faq.question"></span>
                    </h4>
                    <div
                        class="text-base mt-2 leading-8 text-gray-900 collapse-content"
                        v-show="faq.toggled"
                        v-html="faq.answer"
                    ></div>
                </div>
            </div>
        </div>

        <div :id="`panel-reviews`" role="tabpanel" v-show="selectedTab === 'reviews'">
            <div class="font-normal leading-relaxed text-base">
                <review-list :user="user" :recaptcha-key="recaptchaKey" :product="product" />
            </div>
        </div>
    </div>
</template>
